import React, { useEffect, useRef, useCallback } from "react";
import "./ConsentBanner.css";
import { useT } from "../../i18n/useT";
import { CONSENT_UX_FIX_ENABLED } from "../../constants/featureFlags";

/**
 * Accessible GDPR cookie/analytics consent banner.
 *
 * Accessibility (a roadmap acceptance criterion):
 *   - role="dialog" + aria-modal, labelled/described by its heading + body.
 *   - Keyboard: focus moves into the banner on mount, Tab/Shift+Tab are trapped
 *     within Accept/Reject (so keyboard users can't lose the choice behind it),
 *     and the focus ring is always visible.
 *   - Touch targets are >= 44x44px (CSS min-height/min-width).
 *   - AA contrast: dark text/buttons on light surface (see ConsentBanner.css).
 *   - prefers-reduced-motion: the slide-up animation is removed.
 *   - No color-only signalling — actions are labelled text buttons.
 *
 * This component loads NO analytics. GA is initialized by ConsentContext only
 * after `onAccept` fires.
 */
export default function ConsentBanner({ onAccept, onReject }) {
  // i18n-ready: strings resolve from i18n/locales/en/consent.json. Flag-OFF /
  // English-pinned, so every t() returns the exact same literal shown before.
  const { t } = useT("consent");
  const containerRef = useRef(null);
  const acceptBtnRef = useRef(null);
  const rejectBtnRef = useRef(null);

  // Move focus into the banner on mount so keyboard/SR users land on the choice.
  useEffect(() => {
    if (acceptBtnRef.current) {
      acceptBtnRef.current.focus();
    }
  }, []);

  // Trap Tab focus between the two action buttons (a 2-stop loop). All four
  // boundary transitions are handled explicitly here rather than leaning on
  // the browser's default tab order for half of them — that's what let two
  // of the four leak past the banner before (A11Y-1): default tab order only
  // covers the "interior" hop, never the wrap at either end.
  const handleKeyDown = useCallback((event) => {
    if (event.key !== "Tab") {
      return;
    }
    const accept = acceptBtnRef.current;
    const reject = rejectBtnRef.current;
    if (!accept || !reject) {
      return;
    }
    const active = document.activeElement;
    if (event.shiftKey) {
      if (active === reject) {
        // Shift+Tab from the first stop (Reject) wraps to the last (Accept).
        event.preventDefault();
        accept.focus();
      } else if (active === accept) {
        // Shift+Tab from the last stop (Accept) moves to the first (Reject).
        event.preventDefault();
        reject.focus();
      }
    } else {
      if (active === accept) {
        // Tab from the last stop (Accept) wraps to the first (Reject).
        event.preventDefault();
        reject.focus();
      } else if (active === reject) {
        // Tab from the first stop (Reject) moves to the last (Accept).
        event.preventDefault();
        accept.focus();
      }
    }
  }, []);

  // Flag ON adds the `--fix` modifier that kills the ~332px of dead vertical
  // space at mobile widths (see featureFlags.js CONSENT_UX_FIX_ENABLED). Flag
  // OFF -> className is exactly "iyuga-consent-banner", byte-identical to today.
  const rootClassName = CONSENT_UX_FIX_ENABLED
    ? "iyuga-consent-banner iyuga-consent-banner--fix"
    : "iyuga-consent-banner";

  return (
    <div
      className={rootClassName}
      role="dialog"
      aria-modal="true"
      aria-labelledby="iyuga-consent-title"
      aria-describedby="iyuga-consent-desc"
      ref={containerRef}
      onKeyDown={handleKeyDown}
    >
      <div className="iyuga-consent-banner__inner">
        <div className="iyuga-consent-banner__text">
          <h2 id="iyuga-consent-title" className="iyuga-consent-banner__title">
            {t("banner.title")}
          </h2>
          <p id="iyuga-consent-desc" className="iyuga-consent-banner__desc">
            {t("banner.description")}
          </p>
        </div>
        <div className="iyuga-consent-banner__actions">
          <button
            type="button"
            ref={rejectBtnRef}
            className="iyuga-consent-banner__btn iyuga-consent-banner__btn--secondary"
            onClick={onReject}
          >
            {t("banner.reject")}
          </button>
          <button
            type="button"
            ref={acceptBtnRef}
            className="iyuga-consent-banner__btn iyuga-consent-banner__btn--primary"
            onClick={onAccept}
          >
            {t("banner.accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
